import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LoaderCircle, Sparkles, CloudCog, Globe } from "lucide-react";
import { t, useLang, fmtMoney } from "../../i18n";
import { useMasters } from "../form/engine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const Sample = () => {
  const lang = useLang();
  return (
    <span className="ml-2 rounded-full bg-[#22C55E]/10 px-2 py-0.5 font-jbmono text-[8px] font-bold uppercase tracking-widest text-[#22C55E]">
      {t(lang, "sample")}
    </span>
  );
};

const Widget = ({ emoji, label, value, hint, sample }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl">
    <span className="text-2xl">{emoji}</span>
    <p className="mt-2 font-display text-xl font-extrabold">{value}</p>
    <p className="mt-0.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">
      {label}
      {sample && <Sample />}
    </p>
    {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
  </div>
);

const HUBS = {
  student: "StudentVerse", customer: "CustomerHub", chef: "ChefHub", cloud_kitchen: "KitchenHub",
  farmer: "FarmVerse", vendor: "ShopHub", creator: "CreatorStudio", mentor: "LearnHub",
  recruiter: "RecruitHub", hr: "TalentHub", travel_partner: "TravelHub", cafe_owner: "CafeHub",
  rider: "RiderHub", school_admin: "SchoolHub",
  doctor: "HealthHub", nutritionist: "NutritionHub",
};

const ROLE_NAMES = {
  student: "🎓 Student", customer: "🛍️ Customer", chef: "👨‍🍳 Chef", cloud_kitchen: "🥘 Cloud Kitchen",
  farmer: "🌾 Farmer", vendor: "🏪 Vendor", creator: "🎤 Creator", mentor: "🧑‍🏫 Mentor",
  recruiter: "🔎 Recruiter", hr: "🏢 Corporate HR", travel_partner: "✈️ Travel Partner",
  cafe_owner: "☕ Cafe Owner", rider: "🛵 Rider", school_admin: "🏫 School Admin",
  doctor: "🩺 Doctor", nutritionist: "🥗 Nutritionist",
};

const EnterpriseSync = ({ user }) => {
  const lang = useLang();
  const integ = user.integrations || {};
  if (!integ.sap_object) return null;
  return (
    <section className="rounded-3xl border border-[#7C3AED]/25 bg-[#7C3AED]/[0.05] p-5 backdrop-blur-xl" data-testid="enterprise-sync">
      <p className="flex items-center gap-2 font-display text-sm font-bold">
        <CloudCog className="h-4 w-4 text-[#A78BFA]" /> {t(lang, "ent_sync")}
        <span className="ml-auto rounded-full bg-[#22C55E]/10 px-2.5 py-0.5 font-jbmono text-[9px] font-bold uppercase tracking-widest text-[#22C55E] rtl:ml-0 rtl:mr-auto" data-testid="sync-status">
          {t(lang, "ent_queued")}
        </span>
      </p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-[#09090B]/60 p-3">
          <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{t(lang, "zoho_record")}</p>
          <p className="mt-1 text-xs font-bold text-slate-200">{(integ.zoho_modules || []).join(" + ")}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#09090B]/60 p-3">
          <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{t(lang, "sap_object")}</p>
          <p className="mt-1 text-xs font-bold text-slate-200">{integ.sap_object}</p>
        </div>
      </div>
      <p className="mt-2.5 font-jbmono text-[9px] uppercase tracking-widest text-slate-600">
        {t(lang, "ent_hint")}
      </p>
    </section>
  );
};

const FunziPicks = () => {
  const lang = useLang();
  const [recs, setRecs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/personalize`, headers()).then((r) => setRecs(r.data.recommendations)).catch(() => setRecs([]));
  }, []);
  return (
    <section className="rounded-3xl border border-[#06B6D4]/20 bg-[#06B6D4]/[0.04] p-5 backdrop-blur-xl" data-testid="funzi-picks">
      <p className="flex items-center gap-2 font-display text-sm font-bold">
        <Sparkles className="h-4 w-4 text-[#06B6D4]" /> {t(lang, "funzi_picks")}
      </p>
      {recs === null ? (
        <LoaderCircle className="mt-4 h-5 w-5 animate-spin text-[#06B6D4]" />
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {recs.map((r, i) => (
            <div key={i} data-testid={`funzi-pick-${i}`} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-[#09090B]/60 p-3.5">
              <span className="text-xl">{r.emoji}</span>
              <div className="min-w-0">
                <p className="text-xs font-bold">
                  <span className="mr-1.5 rounded-full bg-white/5 px-2 py-0.5 font-jbmono text-[8px] uppercase tracking-wider text-[#06B6D4]">{r.type}</span>
                  {r.title}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">{r.why}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// Business Command Center — real revenue stats + FUNZI predictions (business roles)
const CommandCenter = () => {
  const lang = useLang();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    axios.get(`${API}/command-center`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const stats = [
    { label: t(lang, "cc_revenue"), value: `₹${data.revenue_14d.toLocaleString("en-IN")}` },
    { label: t(lang, "cc_pending"), value: `₹${data.pending.toLocaleString("en-IN")}` },
    { label: t(lang, "cc_growth"), value: `${data.growth_pct > 0 ? "+" : ""}${data.growth_pct}%` },
    { label: t(lang, "cc_orders"), value: data.orders_7d },
  ];
  const preds = [
    ["cc_outlook", data.predictions.growth_outlook],
    ["cc_loan", data.predictions.loan_eligibility],
    ["cc_hiring", data.predictions.hiring_need],
    ["cc_cash", data.predictions.cash_note],
  ];
  return (
    <section className="rounded-3xl border border-[#22C55E]/25 bg-[#22C55E]/[0.04] p-5 backdrop-blur-xl" data-testid="command-center">
      <p className="font-display text-sm font-bold">📡 {t(lang, "cc_title")}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-white/10 bg-[#09090B]/60 p-3">
            <p className="font-display text-lg font-extrabold">{s.value}</p>
            <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {preds.map(([k, v]) => (
          <p key={k} className="rounded-xl border border-white/5 bg-[#09090B]/40 px-3.5 py-2.5 text-[11px] text-slate-300" data-testid={`cc-${k}`}>
            <span className="font-jbmono text-[9px] uppercase tracking-widest text-[#22C55E]">🤖 {t(lang, k)}: </span>{v}
          </p>
        ))}
      </div>
    </section>
  );
};

// Country-aware "Local exclusives" — Personal Chef in the US, ChefRider in India,
// Luxury Villa Dining in the UAE, etc. Offer definitions live in the masters
// collection (country_offers) so admins can tune them without a deploy.
const LocalOffers = ({ user, openModule }) => {
  const lang = useLang();
  const masters = useMasters();
  const offersMap = masters.country_offers || {};
  const country = user.localization?.country || "";
  const offers = offersMap[country] || offersMap.default || [];
  if (!offers.length) return null;
  return (
    <section className="rounded-3xl border border-[#F97316]/25 bg-[#F97316]/[0.04] p-5 backdrop-blur-xl" data-testid="country-offers">
      <p className="flex items-center gap-2 font-display text-sm font-bold">
        <Globe className="h-4 w-4 text-[#F97316]" /> {t(lang, "local_offers")}
        <span className="ml-auto font-jbmono text-[9px] uppercase tracking-widest text-slate-500 rtl:ml-0 rtl:mr-auto">
          {user.localization?.flag} {t(lang, "local_offers_hint")}
        </span>
      </p>
      <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {offers.map((o) => (
          <button
            key={o.id}
            data-testid={`offer-${o.id}`}
            onClick={() => openModule(o.module)}
            className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-[#09090B]/60 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#F97316]/50 rtl:text-right"
          >
            <span className="text-2xl transition-transform duration-300 group-hover:scale-125">{o.emoji}</span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold">{t(lang, o.title_key)}</span>
                {o.price && <span className="shrink-0 font-jbmono text-[10px] font-bold text-[#22C55E]">{o.price}</span>}
              </span>
              <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">{t(lang, o.desc_key)}</span>
              <span className="mt-1.5 block font-jbmono text-[10px] text-[#F97316]">{t(lang, "open_arrow")}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};

export const RoleDashboard = ({ user, openModule }) => {
  const lang = useLang();
  const role = user.role === "user" ? "customer" : user.role;
  const [orders, setOrders] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [shopOrders, setShopOrders] = useState([]);
  const [riderData, setRiderData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orders/mine`, headers()).then((r) => setOrders(r.data.orders)).catch(() => {});
    axios.get(`${API}/applications/mine`, headers()).then((r) => setMyApps(r.data.applications)).catch(() => {});
    if (["chef", "cloud_kitchen"].includes(role)) {
      axios.get(`${API}/menu/mine`, headers()).then((r) => setDishes(r.data.dishes)).catch(() => {});
      axios.get(`${API}/orders/incoming`, headers()).then((r) => setIncoming(r.data.orders)).catch(() => {});
    }
    if (["recruiter", "hr", "admin"].includes(role)) {
      axios.get(`${API}/applications/all`, headers()).then((r) => setAllApps(r.data.applications)).catch(() => {});
    }
    if (role === "vendor") {
      axios.get(`${API}/products/mine`, headers()).then((r) => setMyProducts(r.data.products)).catch(() => {});
      axios.get(`${API}/shop/orders/incoming`, headers()).then((r) => setShopOrders(r.data.orders)).catch(() => {});
    }
    if (role === "rider") {
      axios.get(`${API}/rider/mine`, headers()).then((r) => setRiderData(r.data)).catch(() => {});
    }
  }, [role]);

  const loc = user.localization || {};
  const money = (v) => fmtMoney(v, loc.currency || "INR", loc.symbol || "₹");
  const spent = orders.reduce((a, o) => a + o.total, 0);
  const shortlistedCount = myApps.filter((a) => a.status === "shortlisted").length;
  const chefRevenue = incoming.filter((o) => o.status !== "cancelled").reduce((a, o) => a + o.total, 0);
  const activeOrders = incoming.filter((o) => !["delivered", "cancelled"].includes(o.status)).length;
  const vendorRevenue = shopOrders.filter((o) => o.status !== "cancelled").reduce((a, o) => a + o.total, 0);

  const WIDGETS = {
    student: [
      { emoji: "📚", label: "Courses", value: "3", hint: "Keep the streak alive in LearnBite", sample: true },
      { emoji: "💼", label: "Jobs applied", value: myApps.length, hint: shortlistedCount ? `${shortlistedCount} shortlisted 🎉` : "Recruiters can view & shortlist you" },
      { emoji: "🎥", label: "Reels", value: "0", hint: "Share your first reel from the app", sample: true },
      { emoji: "👥", label: "Communities", value: "2", hint: "AI Builders, Placement Club", sample: true },
      { emoji: "🍔", label: "Food orders", value: orders.length, hint: `${money(spent)} on campus eats` },
      { emoji: "✈️", label: "Travel", value: "Goa?", hint: "Seat sales every Friday", sample: true },
      { emoji: "🎪", label: "Events", value: "4", hint: "Hack night Fri · open mic Sat", sample: true },
      { emoji: "💰", label: "Wallet + Rewards", value: money(2480), hint: "BiteCoins from streaks", sample: true },
    ],
    customer: [
      { emoji: "📦", label: "Orders", value: orders.length, hint: `${money(spent)} lifetime` },
      { emoji: "🎁", label: "Rewards", value: "240 pts", hint: "Redeem on your next order", sample: true },
      { emoji: "❤️", label: "Wishlist", value: "3", hint: "Sneakers restock soon", sample: true },
    ],
    chef: [
      { emoji: "🔥", label: "Orders", value: activeOrders, hint: "Active on your Kitchen Board" },
      { emoji: "🍽️", label: "Menu", value: dishes.length, hint: "Dishes live on FoodBite" },
      { emoji: "📖", label: "Recipes", value: "9", hint: "3 drafts unpublished", sample: true },
      { emoji: "🌾", label: "Farmer marketplace", value: money(1240), hint: "Bought direct this month", sample: true },
      { emoji: "💰", label: "Revenue", value: money(chefRevenue), hint: "Non-cancelled orders" },
      { emoji: "📈", label: "Analytics", value: "+18%", hint: "Orders vs last week", sample: true },
      { emoji: "⭐", label: "Followers", value: "318", hint: "+26 this week", sample: true },
      { emoji: "💬", label: "Reviews", value: "4.9 ★", hint: "87 ratings", sample: true },
    ],
    cloud_kitchen: [
      { emoji: "🔥", label: "Orders", value: activeOrders, hint: "Live queue" },
      { emoji: "🍽️", label: "Menu items", value: dishes.length, hint: "Live on FoodBite" },
      { emoji: "🏭", label: "Capacity", value: user.profile?.capacity || "—", hint: "Daily throughput" },
      { emoji: "💰", label: "Revenue", value: money(chefRevenue), hint: "Non-cancelled orders" },
      { emoji: "📈", label: "Analytics", value: "+11%", hint: "Week over week", sample: true },
    ],
    farmer: [
      { emoji: "🥕", label: "Products", value: "8", hint: "Seasonal vegetables", sample: true },
      { emoji: "📊", label: "Demand forecast", value: "+30%", hint: "Tomatoes surge next week", sample: true },
      { emoji: "🧾", label: "Orders", value: "17", hint: "3 from campus chefs", sample: true },
      { emoji: "💳", label: "Payments", value: money(32800), hint: "Direct sales, no middlemen", sample: true },
      { emoji: "📍", label: "Nearby buyers", value: "9", hint: "Chefs & cafes within 5 km", sample: true },
      { emoji: "🚚", label: "Transport requests", value: "2", hint: "Pickups scheduled", sample: true },
    ],
    vendor: [
      { emoji: "📦", label: "Products", value: myProducts.filter((p) => p.active).length, hint: "Live on ShopBite" },
      { emoji: "🧾", label: "Orders", value: shopOrders.length, hint: `${shopOrders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length} to fulfil` },
      { emoji: "↩️", label: "Returns", value: "1", hint: "Size exchange pending", sample: true },
      { emoji: "🏬", label: "Inventory", value: myProducts.reduce((a, p) => a + p.stock, 0), hint: "Units in stock" },
      { emoji: "🧑‍🤝‍🧑", label: "Customers", value: "64", hint: "Repeat rate 38%", sample: true },
      { emoji: "💰", label: "Revenue", value: money(vendorRevenue), hint: "Non-cancelled orders" },
    ],
    creator: [
      { emoji: "🎥", label: "Reels", value: "14", hint: "48.2K views · 30 days", sample: true },
      { emoji: "📦", label: "Products", value: "3", hint: "Merch linked to reels", sample: true },
      { emoji: "🎓", label: "Course sales", value: money(4200), hint: "Your editing masterclass", sample: true },
      { emoji: "🔗", label: "Affiliate sales", value: money(2100), hint: "ShopBite links", sample: true },
      { emoji: "💰", label: "Revenue", value: money(6450), hint: "Creator fund + tips", sample: true },
      { emoji: "⭐", label: "Followers", value: "3,120", hint: "+180 this week", sample: true },
    ],
    mentor: [
      { emoji: "🎓", label: "Learners", value: "86", hint: "Across 3 subjects", sample: true },
      { emoji: "📚", label: "Courses", value: "2", hint: "DSA live · UX in draft", sample: true },
      { emoji: "💰", label: "Earnings", value: money(9800), hint: "This month", sample: true },
      { emoji: "💬", label: "Reviews", value: "4.8 ★", hint: "52 ratings", sample: true },
    ],
    recruiter: [
      { emoji: "🧑‍🎓", label: "Candidates", value: allApps.length, hint: "Search & review on your board" },
      { emoji: "🗓️", label: "Interviews", value: "3", hint: "To be scheduled", sample: true },
      { emoji: "💼", label: "Jobs posted", value: "4", hint: "STEP, SDE, Explorer, Design" },
      { emoji: "⭐", label: "Shortlists", value: allApps.filter((a) => a.status === "shortlisted").length, hint: "Candidates alerted instantly" },
      { emoji: "📈", label: "Analytics", value: "68%", hint: "Offer-to-join ratio", sample: true },
    ],
    hr: [
      { emoji: "💼", label: "Jobs", value: "4", hint: "Open right now" },
      { emoji: "🧑‍🎓", label: "Candidate pipeline", value: allApps.length, hint: "Review on the Recruiter Board" },
      { emoji: "📣", label: "Referrals", value: "12", hint: "This month", sample: true },
      { emoji: "📢", label: "Hiring campaigns", value: "2", hint: "Intern drive live", sample: true },
      { emoji: "🗓️", label: "Interview scheduling", value: "5", hint: "This week", sample: true },
      { emoji: "📄", label: "Offers", value: "2", hint: "1 accepted · 1 pending", sample: true },
    ],
    travel_partner: [
      { emoji: "🎒", label: "Packages", value: "6", hint: "3 active listings", sample: true },
      { emoji: "🎫", label: "Bookings", value: "41", hint: "Goa week filling fast", sample: true },
      { emoji: "🏨", label: "Hotels", value: "12", hint: "Partner properties", sample: true },
      { emoji: "✈️", label: "Flights", value: "4", hint: "Group deals negotiated", sample: true },
      { emoji: "🗺️", label: "Tours", value: "7", hint: "Guided campus tours", sample: true },
      { emoji: "🧑‍🤝‍🧑", label: "Customers", value: "118", hint: "Lifetime travellers", sample: true },
      { emoji: "💰", label: "Revenue", value: "₹1.2L", hint: "This semester", sample: true },
    ],
    cafe_owner: [
      { emoji: "🍔", label: "Food sales", value: incoming.length || orders.length, hint: "Orders through FUNZ" },
      { emoji: "🌿", label: "Organic sales", value: money(4300), hint: "This month", sample: true },
      { emoji: "🎪", label: "Events", value: "3", hint: "Open mic · board games", sample: true },
      { emoji: "🛋️", label: "Counselling", value: "9", hint: "Wellness corner sessions", sample: true },
      { emoji: "☕", label: "Community meetups", value: "5", hint: "Study groups weekly", sample: true },
      { emoji: "📦", label: "Inventory", value: "92%", hint: "Stock health", sample: true },
    ],
    rider: [
      { emoji: "📦", label: "Deliveries", value: riderData?.earnings.count ?? 0, hint: "Completed all-time" },
      { emoji: "🛣️", label: "Trips", value: riderData?.active.length ?? 0, hint: "Active right now" },
      { emoji: "💰", label: "Earnings", value: money(riderData?.earnings.total ?? 0), hint: `${money(riderData?.earnings.today ?? 0)} today` },
      { emoji: "⭐", label: "Ratings", value: "4.9 ★", hint: "41 ratings", sample: true },
      { emoji: "🗺️", label: "Route optimization", value: "-18%", hint: "Fuel saved vs naive routes", sample: true },
    ],
    school_admin: [
      { emoji: "🧑‍🎓", label: "Students", value: "1,240", hint: "On FUNZ", sample: true },
      { emoji: "🎪", label: "Events", value: "6", hint: "This term", sample: true },
      { emoji: "🍔", label: "Canteen orders", value: "312", hint: "This week", sample: true },
      { emoji: "📊", label: "Reports", value: "4", hint: "Ready to export", sample: true },
    ],
  };

  const widgets = WIDGETS[role] || WIDGETS.customer;

  const SHORTCUTS = [
    { id: "food", emoji: "🍔", label: t(lang, "sc_food") },
    { id: "shop", emoji: "🛍️", label: t(lang, "sc_shopping") },
    { id: "travel", emoji: "✈️", label: t(lang, "sc_travel") },
    { id: "learn", emoji: "🎓", label: t(lang, "sc_learn") },
  ];

  return (
    <div data-testid="role-dashboard" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-[#7C3AED]/15 px-4 py-1.5 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#A78BFA]" data-testid="role-badge">
          {ROLE_NAMES[role] || ROLE_NAMES.customer}
        </span>
        <span className="font-display text-sm font-extrabold text-[#06B6D4]" data-testid="hub-name">
          {HUBS[role] || "My FUNZ"}
        </span>
        {(user.profile?.college || user.profile?.kitchen_name || user.profile?.company || user.profile?.agency_name || user.profile?.farm_name || user.profile?.store_name || user.profile?.cafe_name || user.profile?.school_name) && (
          <span className="font-jbmono text-xs text-slate-400">
            {user.profile.college || user.profile.kitchen_name || user.profile.company || user.profile.agency_name || user.profile.farm_name || user.profile.store_name || user.profile.cafe_name || user.profile.school_name}
          </span>
        )}
        {["chef", "cloud_kitchen"].includes(role) && (
          <Link to="/chef" data-testid="goto-chef" className="ml-auto rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] rtl:ml-0 rtl:mr-auto">
            Kitchen Board & Menu →
          </Link>
        )}
        {role === "vendor" && (
          <Link to="/vendor" data-testid="goto-vendor" className="ml-auto rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-white rtl:ml-0 rtl:mr-auto">
            Vendor Hub →
          </Link>
        )}
        {role === "rider" && (
          <Link to="/rider" data-testid="goto-rider" className="ml-auto rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-white rtl:ml-0 rtl:mr-auto">
            Rider Dispatch →
          </Link>
        )}
        {["recruiter", "hr", "admin"].includes(role) && (
          <Link to="/recruiter" data-testid="goto-recruiter" className="ml-auto rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-white rtl:ml-0 rtl:mr-auto">
            Recruiter Board →
          </Link>
        )}
      </div>

      <FunziPicks />

      <CommandCenter />

      <LocalOffers user={user} openModule={openModule} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {widgets.map((w) => (
          <Widget key={w.label} {...w} />
        ))}
      </div>

      <EnterpriseSync user={user} />

      {(role === "customer" || role === "student") && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="customer-shortcuts">
          {SHORTCUTS.map((s) => (
            <button
              key={s.id}
              data-testid={`shortcut-${s.id}`}
              onClick={() => openModule(s.id)}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-left transition-all hover:-translate-y-1 hover:border-[#7C3AED]/50 rtl:text-right"
            >
              <span className="text-2xl">{s.emoji}</span>
              <p className="mt-2 text-sm font-bold">{s.label}</p>
              <p className="font-jbmono text-[10px] text-[#06B6D4]">{t(lang, "open_arrow")}</p>
            </button>
          ))}
        </div>
      )}

      {["recruiter", "hr", "admin"].includes(role) && allApps.length > 0 && (
        <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="recent-candidates">
          <h3 className="font-display text-lg font-bold">{t(lang, "latest_candidates")}</h3>
          <div className="mt-4 space-y-2.5">
            {allApps.slice(0, 4).map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-3.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] text-xs font-black">
                  {a.applicant.avatar || (a.applicant.name || a.applicant.email)[0].toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold">{a.applicant.name || a.applicant.email}</p>
                  <p className="truncate font-jbmono text-[10px] text-slate-500">{a.job} @ {a.company}</p>
                </div>
                <span className="rounded-full bg-white/5 px-2.5 py-1 font-jbmono text-[9px] uppercase tracking-wider text-slate-400">{a.status}</span>
              </div>
            ))}
          </div>
          <Link to="/recruiter" className="mt-4 inline-block font-jbmono text-[11px] text-[#06B6D4] hover:underline">
            {t(lang, "review_all")}
          </Link>
        </section>
      )}

      <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
        {t(lang, "widgets_note")}
      </p>
    </div>
  );
};
